import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  DocumentIcon,
  FolderIcon,
  TagIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import DocumentList from './DocumentList';
import DocumentUpload from './DocumentUpload';
import DocumentViewer from './DocumentViewer';
import documentService from '../../services/documentService';

const DocumentCenter = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    subcategory: '',
    search: '',
    isPublic: '',
    isActive: ''
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchDocuments();
  }, [filters]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${import.meta.env.VITE_API_URL}/document-center?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.data.documents || []);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentView = (document) => {
    setSelectedDocument(document);
    setShowViewer(true);
  };

  const handleDocumentDownload = async (doc) => {
    try {
      console.log('📥 Client: Starting download for document:', doc.id);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/document-center/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📥 Client: Response status:', response.status);
      console.log('📥 Client: Response headers:', response.headers);

      if (response.ok) {
        const blob = await response.blob();
        console.log('📥 Client: Blob received:', {
          size: blob.size,
          type: blob.type
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.fileName;
        console.log('📥 Client: Triggering download with filename:', doc.fileName);
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success('Document downloaded successfully');
      } else {
        const errorText = await response.text();
        console.log('📥 Client: Error response:', errorText);
        toast.error('Failed to download document');
      }
    } catch (error) {
      console.error('📥 Client: Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDocumentApprove = async (doc) => {
    try {
      const res = await documentService.approve(doc.id);
      toast.success('Document approved');
      fetchDocuments();
    } catch (e) {
      console.error(e);
      toast.error('Approval failed');
    }
  };

  const handleDocumentUpload = () => {
    setShowUpload(true);
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    fetchDocuments();
    toast.success('Document uploaded successfully');
  };

  const categories = [
    { value: 'policy', label: 'Policies' },
    { value: 'form', label: 'Forms' },
    { value: 'template', label: 'Templates' },
    { value: 'procedure', label: 'Procedures' },
    { value: 'guideline', label: 'Guidelines' },
    { value: 'other', label: 'Other' }
  ];

  const subcategories = {
    policy: ['HR Policy', 'IT Policy', 'Security Policy', 'Leave Policy'],
    form: ['Leave Request', 'Expense Form', 'Timesheet', 'Performance Review'],
    template: ['Email Template', 'Report Template', 'Contract Template'],
    procedure: ['Onboarding', 'Offboarding', 'Performance Review', 'Disciplinary'],
    guideline: ['Best Practices', 'Standards', 'Processes'],
    other: ['Miscellaneous', 'General']
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Document Center
            </h1>
            <p className="text-gray-600">
              Access and manage company documents, policies, and templates
            </p>
          </motion.div>
        </div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="w-8 h-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Documents</p>
                <p className="text-2xl font-semibold text-gray-900">{documents.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderIcon className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Categories</p>
                <p className="text-2xl font-semibold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <EyeIcon className="w-8 h-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Public Documents</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {documents.filter(d => d.isPublic).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="w-8 h-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Recent Uploads</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {documents.filter(d => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(d.createdAt) > weekAgo;
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters and Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              <select
                value={filters.subcategory}
                onChange={(e) => setFilters({ ...filters, subcategory: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subcategories</option>
                {filters.category && subcategories[filters.category]?.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>

              <select
                value={filters.isPublic}
                onChange={(e) => setFilters({ ...filters, isPublic: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Documents</option>
                <option value="true">Public Only</option>
                <option value="false">Private Only</option>
              </select>
            </div>

            {/* Upload Button */}
            {(user?.Role?.name === 'admin' || user?.Role?.name === 'hr' || user?.role?.name === 'admin' || user?.role?.name === 'hr') && (
              <button
                onClick={handleDocumentUpload}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <PlusIcon className="w-5 h-5 inline mr-2" />
                Upload Document
              </button>
            )}
          </div>
        </motion.div>

        {/* Document List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <DocumentList
            documents={documents}
            onView={handleDocumentView}
            onDownload={handleDocumentDownload}
            onApprove={handleDocumentApprove}
            user={user}
          />
        </motion.div>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <DocumentUpload
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
          categories={categories}
          subcategories={subcategories}
        />
      )}

      {/* Document Viewer Modal */}
      {showViewer && selectedDocument && (
        <DocumentViewer
          document={selectedDocument}
          onClose={() => {
            setShowViewer(false);
            setSelectedDocument(null);
          }}
          onDownload={() => handleDocumentDownload(selectedDocument)}
        />
      )}
    </div>
  );
};

export default DocumentCenter; 